import React, {useEffect, useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {dayTimesOf, normalizeDayTimes, toWireDayTimes} from '@/entities/attendance/schedule';
import type {DayTimeInput} from '@/entities/attendance/types';
import type {AttendanceSchedule} from '@/entities/personnel/types';
import {WeekScheduleEditor} from '@/features/manage-schedule/ui/WeekScheduleEditor';
import {AppInput} from '@/shared/ui/input';
import {AppText} from '@/shared/ui/text';
import {Toggle} from '@/shared/ui/toggle';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * A compact attendance-schedule editor. Off ⇒ the person has no personal schedule
 * (the department's applies, or none). On ⇒ the week table (`WeekScheduleEditor`, shared
 * with the department form): presets, usual time, and each day's own time in its row;
 * the grace/overtime thresholds sit behind a "Дополнительно"
 * disclosure with sensible defaults, so the common case stays two taps.
 *
 * Local state is seeded once from `value` and emitted through onChange — the parent
 * form re-mounts it via `key` when switching person, so a stale schedule never leaks
 * from one edit into the next.
 */
const DEFAULTS = {
  late_grace_minutes: 5,
  early_arrival_threshold_minutes: 30,
  early_leave_grace_minutes: 5,
  overtime_threshold_minutes: 30,
};

/** A minutes text field → non-negative int, blank/garbage → 0. */
function parseMinutes(text: string): number {
  const n = parseInt(text.replace(/[^\d]/g, ''), 10);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

const styles = StyleSheet.create({
  root: {gap: spacing(4)},
  disclosure: {alignSelf: 'flex-start'},
  grid: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(3)},
  gridItem: {flexBasis: '47%', flexGrow: 1},
});

export function ScheduleEditor({
  value,
  onChange,
}: {
  value: AttendanceSchedule | null | undefined;
  onChange: (schedule: AttendanceSchedule | null) => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();

  const [enabled, setEnabled] = useState(!!value);
  const [mask, setMask] = useState(value?.workdays_mask ?? 0b0011111); // Mon–Fri
  const [startMin, setStartMin] = useState(value?.start_minutes ?? 9 * 60);
  const [endMin, setEndMin] = useState(value?.end_minutes ?? 18 * 60);
  const [advanced, setAdvanced] = useState(false);
  const [dayTimes, setDayTimes] = useState<DayTimeInput[]>(() => dayTimesOf(value));
  const [lateGrace, setLateGrace] = useState(
    value?.late_grace_minutes ?? DEFAULTS.late_grace_minutes,
  );
  const [earlyArrival, setEarlyArrival] = useState(
    value?.early_arrival_threshold_minutes ?? DEFAULTS.early_arrival_threshold_minutes,
  );
  const [earlyLeave, setEarlyLeave] = useState(
    value?.early_leave_grace_minutes ?? DEFAULTS.early_leave_grace_minutes,
  );
  const [overtime, setOvertime] = useState(
    value?.overtime_threshold_minutes ?? DEFAULTS.overtime_threshold_minutes,
  );

  useEffect(() => {
    if (!enabled) {
      onChange(null);
      return;
    }
    onChange({
      workdays_mask: mask,
      start_minutes: startMin,
      end_minutes: endMin,
      late_grace_minutes: lateGrace,
      early_arrival_threshold_minutes: earlyArrival,
      early_leave_grace_minutes: earlyLeave,
      overtime_threshold_minutes: overtime,
      day_times: toWireDayTimes(normalizeDayTimes(dayTimes, mask, startMin, endMin)),
    });
    // onChange identity is stable enough (parent memoises via setState); deps are the
    // fields that define the schedule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, mask, startMin, endMin, lateGrace, earlyArrival, earlyLeave, overtime, dayTimes]);

  return (
    <View style={styles.root}>
      {/* A real switch, not a chip that toggles its own label: "График" on/off is the
          same kind of decision as every other Toggle in the app and should look it. */}
      <Toggle
        label={t('personnel.scheduleLabel')}
        hint={t(enabled ? 'personnel.scheduleOn' : 'personnel.scheduleOff')}
        value={enabled}
        onChange={setEnabled}
      />

      {enabled ? (
        <>
          <WeekScheduleEditor
            value={{mask, start: startMin, end: endMin, dayTimes}}
            onChange={w => {
              setMask(w.mask);
              // A personal schedule always has times; the editor only offers them.
              setStartMin(w.start ?? startMin);
              setEndMin(w.end ?? endMin);
              setDayTimes(w.dayTimes);
            }}
          />

          <Pressable
            onPress={() => setAdvanced(v => !v)}
            accessibilityRole="button"
            accessibilityState={{expanded: advanced}}
            style={styles.disclosure}>
            <AppText variant="label" color={p.primary}>
              {t(advanced ? 'personnel.scheduleLess' : 'personnel.scheduleMore')}
            </AppText>
          </Pressable>

          {advanced ? (
            <View style={styles.grid}>
              <AppInput
                label={t('personnel.lateGrace')}
                value={String(lateGrace)}
                onChangeText={text => setLateGrace(parseMinutes(text))}
                keyboardType="number-pad"
                style={styles.gridItem}
              />
              <AppInput
                label={t('personnel.earlyArrival')}
                value={String(earlyArrival)}
                onChangeText={text => setEarlyArrival(parseMinutes(text))}
                keyboardType="number-pad"
                style={styles.gridItem}
              />
              <AppInput
                label={t('personnel.earlyLeave')}
                value={String(earlyLeave)}
                onChangeText={text => setEarlyLeave(parseMinutes(text))}
                keyboardType="number-pad"
                style={styles.gridItem}
              />
              <AppInput
                label={t('personnel.overtime')}
                value={String(overtime)}
                onChangeText={text => setOvertime(parseMinutes(text))}
                keyboardType="number-pad"
                style={styles.gridItem}
              />
            </View>
          ) : null}
        </>
      ) : null}
    </View>
  );
}
